import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabaseClient'
import Header from '../components/Header'

function ProjectProgressItem({ projectId, fallbackPercent }) {
  const [percent, setPercent] = React.useState(fallbackPercent)
  React.useEffect(() => {
    let mounted = true
    const run = async () => {
      try {
        const { data, error } = await supabase
          .from('tasks')
          .select('id,status')
          .eq('project_id', projectId)
        if (!error && data && mounted) {
          const total = data.length
          const completed = data.filter(t => t.status === 'completed').length
          const pct = total > 0 ? Math.round((completed / total) * 100) : 0
          setPercent(pct)
        }
      } catch {}
    }
    run()
    return () => { mounted = false }
  }, [projectId])

  return (
    <>
      <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
        <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${percent}%` }}></div>
      </div>
      <span className="text-sm font-semibold text-gray-900">{percent}%</span>
    </>
  )
}

const ClientDashboard = () => {
  const { user, profile, signOut, loading: authLoading } = useAuthStore()
  const [activeTab, setActiveTab] = useState('overview')
  const [clientOrders, setClientOrders] = useState([])
  const [clientProjects, setClientProjects] = useState([])
  const [clientInvoices, setClientInvoices] = useState([])
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [error, setError] = useState(null)
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    company: '',
    address: '',
    bio: ''
  })
  const [profilePicture, setProfilePicture] = useState(null)
  const [profilePictureUrl, setProfilePictureUrl] = useState('')
  const [clientTickets, setClientTickets] = useState([])
  const [ticketReplies, setTicketReplies] = useState([])
  const [sendingTicket, setSendingTicket] = useState(false)
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' })
  const [viewingTicket, setViewingTicket] = useState(null)
  const [replyText, setReplyText] = useState('')

  useEffect(() => {
    if (!authLoading && user && profile) {
      fetchClientData()
      loadProfileData()
    }
  }, [user, profile, authLoading])

  const fetchClientData = async () => {
    setDashboardLoading(true)
    setError(null)
    try {
      // Fetch Orders
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('client_id', user.id)
        .order('created_at', { ascending: false })
      
      if (ordersError) {
        console.warn('Orders fetch error:', ordersError)
        if (ordersError.code === 'PGRST205') {
          console.log('📋 Orders table not found. Please run the SQL script from URGENT_DATABASE_FIX.md')
        }
        setClientOrders([])
      } else {
        setClientOrders(orders || [])
      }

      // Fetch Projects
      // Fetch client support tickets (same pattern as AdminDashboard)
      try {
        const { data: tickets, error: tErr } = await supabase.rpc('st_list_tickets', { p_client_id: user.id })
        if (tErr) {
          console.warn('Tickets fetch error:', tErr)
          setClientTickets([])
        } else {
          setClientTickets(tickets || [])
        }
      } catch (e) {
        console.warn('Tickets fetch exception:', e)
        setClientTickets([])
      }
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('client_id', user.id)
        .order('created_at', { ascending: false })
      
      if (projectsError) {
        console.warn('Projects fetch error:', projectsError)
        if (projectsError.code === 'PGRST205') {
          console.log('📋 Projects table not found. Please run the SQL script from URGENT_DATABASE_FIX.md')
        }
        setClientProjects([])
      } else {
        setClientProjects(projects || [])
      }

      // Fetch Invoices (includes legacy rows without client_id)
      let mergedInvoices = []
      try {
        // Path 1: direct client_id match (new rows)
        const { data: directInvoices } = await supabase
          .from('invoices')
          .select('*, project:project_id(name, client_id)')
          .eq('client_id', user.id)
          .order('created_at', { ascending: false })
        mergedInvoices = directInvoices || []
      } catch {}

      try {
        // Path 2: project-based (legacy rows without client_id)
        const projectIds = (clientProjects || []).map(p => p.id)
        if (projectIds.length > 0) {
          const { data: projInvoices } = await supabase
            .from('invoices')
            .select('*, project:project_id(name, client_id)')
            .in('project_id', projectIds)
            .order('created_at', { ascending: false })
          const existingIds = new Set(mergedInvoices.map(i => i.id))
          ;(projInvoices || []).forEach(inv => { if (!existingIds.has(inv.id)) mergedInvoices.push(inv) })
        }
      } catch (invoicesError) {
        console.warn('Invoices fetch error:', invoicesError)
      }

      setClientInvoices(mergedInvoices)

    } catch (err) {
      console.error('Error fetching client dashboard data:', err)
      setError('Failed to load dashboard data.')
    } finally {
      setDashboardLoading(false)
    }
  }

  const submitTicket = async (e) => {
    e.preventDefault()
    try {
      setSendingTicket(true)
      const subject = (newTicket.subject || '').toString().trim()
      const message = (newTicket.message || '').toString().trim()
      if (!subject || !message) { alert('Please fill subject and message'); setSendingTicket(false); return }
      // Direct insert (same pattern as AdminDashboard uses for inserts)
      const { data: newId, error: insertErr } = await supabase.rpc('st_create_ticket_json', {
        payload: {
          p_client_id: user.id,
          p_email: user.email,
          p_subject: subject,
          p_message: message
        }
      })
      if (insertErr) throw insertErr
      
      // Refresh tickets list via RPC
      const { data: tickets, error: fetchErr } = await supabase.rpc('st_list_tickets', { p_client_id: user.id })
      if (!fetchErr) {
        setClientTickets(tickets || [])
      }
      setNewTicket({ subject: '', message: '' })
      alert('Ticket submitted successfully!')
    } catch (err) {
      console.error('Submit ticket failed:', err)
      alert(`Failed to submit ticket: ${err.message || 'Unknown error'}`)
    } finally {
      setSendingTicket(false)
    }
  }

  const loadRepliesFor = async (ticket) => {
    setViewingTicket(ticket)
    try {
      const { data: reps } = await supabase.rpc('st_list_messages', { p_ticket_id: ticket.id })
      setTicketReplies(reps || [])
    } catch { setTicketReplies([]) }
  }

  const sendClientReply = async (e) => {
    e.preventDefault()
    if (!viewingTicket) return
    const msg = (replyText || '').toString().trim()
    if (!msg) return
    try {
      const { error } = await supabase.rpc('st_add_message', { p_ticket_id: viewingTicket.id, p_sender_role: 'client', p_message: msg })
      if (error) throw error
      setReplyText('')
      await loadRepliesFor(viewingTicket)
    } catch (err) {
      console.error('Reply failed:', err)
      alert(`Failed to reply: ${err.message}`)
    }
  }

  const loadProfileData = async () => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      
      if (!error && profileData) {
        setProfileForm({
          name: profileData.name || '',
          phone: profileData.phone || '',
          company: profileData.company || '',
          address: profileData.address || '',
          bio: profileData.bio || ''
        })
        setProfilePictureUrl(profileData.profile_picture_url || '')
      }
    } catch (err) {
      console.warn('Error loading profile data:', err)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    try {
      const updates = {
        name: profileForm.name,
        phone: profileForm.phone,
        company: profileForm.company,
        address: profileForm.address,
        bio: profileForm.bio,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)

      if (error) throw error

      alert('Profile updated successfully!')
    } catch (err) {
      console.error('Error updating profile:', err)
      alert('Failed to update profile. Please try again.')
    }
  }

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, GIF)')
      return
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('File size must be less than 5MB')
      return
    }

    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      
      console.log('Uploading file:', fileName)
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true // Allow overwriting existing files
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      console.log('Upload successful:', uploadData)

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName)

      console.log('Public URL:', urlData.publicUrl)

      // Update profile with new picture URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture_url: urlData.publicUrl })
        .eq('id', user.id)

      if (updateError) {
        console.error('Profile update error:', updateError)
        throw updateError
      }

      setProfilePictureUrl(urlData.publicUrl)
      alert('Profile picture updated successfully!')
    } catch (err) {
      console.error('Error uploading profile picture:', err)
      if (err.message?.includes('row-level security')) {
        alert('Storage access denied. Please run the storage setup script from STORAGE_FIX.md')
      } else {
        alert(`Failed to upload profile picture: ${err.message}`)
      }
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  if (authLoading || dashboardLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Database Setup Required</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-yellow-800 mb-2">Quick Fix:</h3>
            <ol className="text-sm text-yellow-700 text-left space-y-1">
              <li>1. Go to Supabase Dashboard</li>
              <li>2. Open SQL Editor</li>
              <li>3. Run script from URGENT_DATABASE_FIX.md</li>
              <li>4. Refresh this page</li>
            </ol>
          </div>
          <button 
            onClick={fetchClientData}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white shadow-lg p-6 min-h-[calc(100vh-64px)] border-r border-gray-200">
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-3">
                <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
                <p className="text-sm text-gray-600">Welcome back!</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-4">
              <p className="text-sm font-medium text-primary-800">
                {profile?.name || user?.email}
              </p>
              <p className="text-xs text-primary-600">Client Account</p>
            </div>
          </div>
          
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'overview' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
              </svg>
              Overview
            </button>
            
            <button
              onClick={() => setActiveTab('orders')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'orders' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              My Orders
            </button>
            
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'projects' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              My Projects
            </button>
            
            <button
              onClick={() => setActiveTab('invoices')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'invoices' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Invoices
            </button>
            
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'profile' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Profile Settings
            </button>

            <button
              onClick={() => setActiveTab('support')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center ${
                activeTab === 'support' 
                  ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.88 9h.01M14.12 9h.01M8 13a4 4 0 008 0" />
              </svg>
              <span className="flex items-center">
                Support Tickets
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-600 text-white">
                  {(clientTickets || []).filter(t => (t.status || 'open') === 'answered').length}
                </span>
              </span>
            </button>
            
            <div className="border-t border-gray-200 pt-4 mt-6">
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200 flex items-center"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {activeTab === 'overview' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
                <p className="text-gray-600">Here's what's happening with your projects and orders</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Total Orders</h3>
                      <p className="text-3xl font-bold text-blue-600">{clientOrders.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Active Projects</h3>
                      <p className="text-3xl font-bold text-green-600">{clientProjects.filter(p => p.status === 'active' || p.status === 'in_progress').length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">Pending Invoices</h3>
                      <p className="text-3xl font-bold text-yellow-600">{clientInvoices.filter(i => i.status === 'pending').length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-4">
                  {clientOrders.slice(0, 3).map(order => (
                    <div key={order.id} className="flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div className="ml-4 flex-1">
                        <p className="font-medium text-gray-900">Order #{order.id}</p>
                        <p className="text-sm text-gray-600">৳{order.total_amount?.toLocaleString()}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Tickets</h1>
                <p className="text-gray-600">Create a ticket and chat with support</p>
              </div>

              {/* New Ticket */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Ticket</h2>
                <form onSubmit={submitTicket} className="space-y-3">
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e)=>setNewTicket({...newTicket, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Subject"
                  />
                  <textarea
                    rows={4}
                    value={newTicket.message}
                    onChange={(e)=>setNewTicket({...newTicket, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe your issue"
                  />
                  <div className="flex justify-end">
                    <button type="submit" disabled={sendingTicket} className="btn-primary">{sendingTicket ? 'Submitting…' : 'Submit Ticket'}</button>
                  </div>
                </form>
              </div>

              {/* Ticket List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {clientTickets.length === 0 ? (
                        <tr><td colSpan="4" className="px-6 py-12 text-center text-gray-500">No tickets yet.</td></tr>
                      ) : (
                        clientTickets.map(t => (
                          <tr key={t.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.subject || '(no subject)'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${t.status==='answered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{t.status || 'open'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                              <button className="text-primary-600 hover:text-primary-900" onClick={()=>loadRepliesFor(t)}>View</button>
                              <button className="text-red-600 hover:text-red-900" onClick={async ()=>{
                                if (!window.confirm('Delete this ticket?')) return
                                try {
                                  const { error } = await supabase.rpc('st_delete_ticket', { p_ticket_id: t.id })
                                  if (error) throw error
                                  const { data: tickets } = await supabase.rpc('st_list_tickets', { p_client_id: user.id })
                                  setClientTickets(tickets || [])
                                } catch (e) {
                                  alert(`Delete failed: ${e.message || e}`)
                                }
                              }}>Delete</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ticket Thread Modal */}
              {viewingTicket && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Ticket: {viewingTicket.subject || '(no subject)'}</h2>
                      <button onClick={()=>{ setViewingTicket(null); setTicketReplies([]); setReplyText('') }} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
                      <div className="bg-gray-50 border border-gray-200 rounded p-3">
                        <p className="text-sm text-gray-700"><strong>You</strong> • {viewingTicket.created_at ? new Date(viewingTicket.created_at).toLocaleString() : ''}</p>
                        <p className="text-gray-800 mt-1">{viewingTicket.message}</p>
                      </div>
                      {ticketReplies.map(r => (
                        <div key={r.id} className={`rounded p-3 border ${r.sender_role==='admin' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                          <p className="text-sm text-gray-700"><strong>{r.sender_role==='admin' ? 'Admin' : 'You'}</strong> • {r.created_at ? new Date(r.created_at).toLocaleString() : ''}</p>
                          <p className="text-gray-800 mt-1">{r.message}</p>
                        </div>
                      ))}
                    </div>
                    <form onSubmit={sendClientReply} className="flex gap-2">
                      <input type="text" value={replyText} onChange={(e)=>setReplyText(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Type a reply..." />
                      <button type="submit" className="btn-primary">Send</button>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
                <p className="text-gray-600">Track your order history and status</p>
              </div>
              
              <div className="space-y-4">
                {clientOrders.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
                    <p className="text-gray-500 mb-4">You haven't placed any orders yet.</p>
                    <button className="btn-primary">Browse Products</button>
                  </div>
                ) : (
                  clientOrders.map(order => (
                  <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Order #{order.id}</h3>
                            <p className="text-sm text-gray-600">Placed on {new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Amount</p>
                            <p className="text-lg font-semibold text-gray-900">৳{order.total_amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Payment Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {order.payment_status}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Order Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              order.status === 'completed' ? 'bg-green-100 text-green-800' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Link to={`/order/${order.id}`} className="btn-secondary text-sm">
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Projects</h1>
                <p className="text-gray-600">Track your project progress and milestones</p>
              </div>
              
              <div className="space-y-4">
                {clientProjects.map(project => (
                  <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                            <p className="text-sm text-gray-600">Started on {new Date(project.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Progress</p>
                            <div className="flex items-center">
                              <ProjectProgressItem projectId={project.id} fallbackPercent={project.progress || 0} />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              project.status === 'completed' ? 'bg-green-100 text-green-800' :
                              project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              project.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Budget</p>
                            <p className="text-sm font-semibold text-gray-900">৳{project.budget?.toLocaleString() || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Link to={`/project/${project.id}`} className="btn-primary text-sm">
                          View Project
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Invoices</h1>
                <p className="text-gray-600">View and manage your invoices</p>
              </div>
              
              <div className="space-y-4">
                {clientInvoices.map(invoice => (
                  <div key={invoice.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Invoice #{String(invoice.id).slice(0,8)}</h3>
                            <p className="text-sm text-gray-600">Created on {new Date(invoice.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Amount</p>
                            <p className="text-lg font-semibold text-gray-900">৳{invoice.amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                              invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {invoice.status}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Due Date</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : (invoice.created_at ? new Date(new Date(invoice.created_at).getTime() + 7*24*60*60*1000).toLocaleDateString() : 'N/A')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={async () => {
                          try {
                            const { generateInvoicePdf } = await import('../utils/invoicePdf')
                            await generateInvoicePdf({
                              invoice,
                              project: { id: invoice.project_id, name: invoice.project?.name },
                              client: { id: user.id, name: profile?.name || '' }
                            })
                          } catch (e) {
                            console.error('Client invoice download failed:', e)
                            alert('Failed to generate PDF')
                          }
                        }} className="btn-secondary text-sm">
                          Download PDF
                        </button>
                        {invoice.status === 'pending' ? (
                          <button className="btn-primary text-sm">Pay Now</button>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 self-center">Paid</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Settings</h1>
                <p className="text-gray-600">Manage your account information and profile picture</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Profile Picture Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Profile Picture</h3>
                  
                  <div className="text-center">
                    <div className="relative inline-block">
                      {profilePictureUrl ? (
                        <img 
                          src={profilePictureUrl} 
                          alt="Profile" 
                          className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center border-4 border-gray-200">
                          <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                      
                      <label className="absolute bottom-0 right-0 bg-primary-600 text-white rounded-full p-2 cursor-pointer hover:bg-primary-700 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleProfilePictureUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-4">
                      Click the camera icon to upload a new profile picture
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Supported formats: JPG, PNG, GIF (Max 5MB)
                    </p>
                  </div>
                </div>

                {/* Profile Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h3>
                  
                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                        <input 
                          type="text" 
                          value={profileForm.name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <input 
                          type="tel" 
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="+880 1XXX XXXXXX"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization</label>
                      <input 
                        type="text" 
                        value={profileForm.company}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                      <textarea 
                        value={profileForm.address}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, address: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Your full address"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                      <textarea 
                        value={profileForm.bio}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                    
                    <div className="pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Email Address</p>
                          <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                          <p className="text-xs text-gray-500">Email cannot be changed</p>
                        </div>
                        <button type="submit" className="btn-primary">
                          Update Profile
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Account Security Section */}
              <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Account Security</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Password</h4>
                      <p className="text-sm text-gray-600">Last changed: Never</p>
                    </div>
                    <button className="btn-secondary text-sm">
                      Change Password
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
                      <p className="text-sm text-gray-600">Add extra security to your account</p>
                    </div>
                    <button className="btn-secondary text-sm">
                      Enable 2FA
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default ClientDashboard